import React, { useEffect, useState } from "react";  
import {  
  Card,  
  Typography,  
  Row,  
  Col,  
  Avatar,  
  Space,  
  Button,  
  message,  
  Input,  
  Form,  
  Spin,  
  Checkbox,
  DatePicker
} from "antd";  
import { UploadOutlined } from "@ant-design/icons";  
import dayjs from "dayjs";  
import {  
  searchContestants,  
  updateContestantDetails,  
} from "../../api/contestant";  
import {  
  uploadProfilePhoto,  
  fetchProfilePhoto,  
} from "../../api/user";  
import "./profiles.scss";  
  
const { Title, Text } = Typography;  
  
const validateSouthAfricanIdNumber = (_, value) => {  
  if (!value || value.length !== 13) {  
    return Promise.reject(new Error("ID number must be 13 digits long"));  
  }  
  const year = parseInt(value.slice(0, 2), 10);  
  const month = parseInt(value.slice(2, 4), 10);  
  const day = parseInt(value.slice(4, 6), 10);  
  if (month < 1 || month > 12 || day < 1 || day > 31) {  
    return Promise.reject(new Error("Invalid date in ID number"));  
  }  
  const gender = parseInt(value.slice(6, 10), 10);  
  if (gender < 0 || gender > 9999) {  
    return Promise.reject(new Error("Invalid gender digits in ID number"));  
  }  
  const citizenship = parseInt(value.slice(10, 11), 10);  
  if (citizenship !== 0 && citizenship !== 1) {  
    return Promise.reject(new Error("Invalid citizenship digit in ID number"));  
  }  
  // Luhn check  
  const luhnCheck = (num) => {  
    let sum = 0;  
    for (let i = 0; i < num.length; i++) {  
      let digit = parseInt(num[i], 10);  
      if (i % 2 === num.length % 2) {  
        digit *= 2;  
        if (digit > 9) {  
          digit -= 9;  
        }  
      }  
      sum += digit;  
    }  
    return sum % 10 === 0;  
  };  
  if (!luhnCheck(value)) {  
    return Promise.reject(  
      new Error("Invalid digit in ID number. Please verify your ID number is correct.")  
    );  
  }  
  return Promise.resolve();  
};  
  
const getInitials = (name = "") => {  
  let rgx = new RegExp(/(\p{L}{1})\p{L}+/, "gu");  
  let initials = [...name.matchAll(rgx)] || [];  
  initials =  
    ((initials.shift()?.[1] || "") + (initials.pop()?.[1] || "")).toUpperCase();  
  return initials;  
};  
  
const Profile = () => {  
  const [loading, setLoading] = useState(true);  
  const [contestant, setContestant] = useState(null);  
  const [form] = Form.useForm();  
  
  const [file, setFile] = useState(null);  
  const [photoLoading, setPhotoLoading] = useState(false);  
  const [profilePhotoUrl, setProfilePhotoUrl] = useState(null);  
  const [hasAllergies, setHasAllergies] = useState(false);  
  
  useEffect(() => {  
    const fetchContestant = async () => {  
      setLoading(true);  
      try {  
        const data = await searchContestants();  
        const fetched = data?.data[0];  
        setContestant(fetched);  
  
        // Form initial values  
        form.setFieldsValue({  
          first_name: fetched.first_name,  
          last_name: fetched.last_name,  
          email: fetched.email,  
          gender: fetched.gender,  
          cell_number: fetched.cell_number,  
          work_number: fetched.work_number,  
          id_number: fetched.id_number,  
          passport_number: fetched.passport_number,  
          address: fetched.address?.[0] || {},  
          ice_contact_name: fetched.ice_contact_name,  
          relationship_to_ice: fetched.relationship_to_ice,  
          ice_contact_email: fetched.ice_contact_email,  
          allergies: fetched.allergies,  
          allergies_specification: fetched.allergies_specification,  
          academic_institution: fetched.academic_institution,  
          provincial_director: fetched.provincial_director,  
        });  

        setHasAllergies(!!fetched.allergies);  
  
        // Load profile photo  
        if (fetched && fetched._id) {  
          try {  
            setPhotoLoading(true);  
            const url = await fetchProfilePhoto(fetched._id);  
            setProfilePhotoUrl(url);  
          } catch {  
            setProfilePhotoUrl(null);  
          } finally {  
            setPhotoLoading(false);  
          }  
        }  
      } catch (err) {  
        message.error("Failed to load profile.");  
      } finally {  
        setLoading(false);  
      }  
    };  
    fetchContestant();  
  }, [form]);  
  
  const handleFileChange = (e) => {  
    if (e.target.files?.[0]) setFile(e.target.files[0]);  
  };  
  
  const handleFileUpload = async () => {  
    if (!contestant?._id || !file) return;  
    setPhotoLoading(true);  
    try {  
      await uploadProfilePhoto(contestant._id, file);  
      message.success("Profile photo uploaded successfully!");  
      setFile(null);  
      const newUrl = await fetchProfilePhoto(contestant._id);  
      setProfilePhotoUrl(newUrl);  
    } catch (err) {  
      message.error("Profile photo upload failed");  
    } finally {  
      setPhotoLoading(false);  
    }  
  };  
  
  const handleUpdate = async (values) => {  
    if (!contestant) return;  
    setLoading(true);  
    try {  
      const payload = {  
        ...values,  
        address: [values.address],  
        date_of_birth: values.date_of_birth ? values.date_of_birth.toDate() : null, 
      };  
      await updateContestantDetails(contestant._id, payload);  
      message.success("Contestant details updated!");  
      setContestant((prev) => ({ ...prev, ...payload }));  
    } catch (err) {  
      message.error("Update failed. Please try again.");  
    } finally {  
      setLoading(false);  
    }  
  };  
  
  return (  
    <div className="profile-page">  
      <Row gutter={[16, 16]}>  
        <Col span={24} md={8}>  
          <Card  
            title="Profile"  
            loading={loading}  
            className="profile-avatar-card"  
          >  
            {contestant ? (  
              <Space  
                direction="vertical"  
                align="center"  
                style={{ width: "100%" }}  
              >  
                <Spin spinning={photoLoading}>  
                  <Avatar  
                    size={100}  
                    style={{ fontSize: 30, background: "#1271dd", marginBottom: 16 }}  
                    src={profilePhotoUrl || undefined}  
                  >  
                    {getInitials(  
                      `${contestant.first_name || ""} ${contestant.last_name || ""}`  
                    )}  
                  </Avatar>  
                </Spin>  
                <div>  
                  <input  
                    type="file"  
                    id="profilePhotoInput"  
                    style={{ display: "none" }}  
                    onChange={handleFileChange}  
                    accept=".jpg,.jpeg"  
                  />  
                  <Button  
                    icon={<UploadOutlined />}  
                    onClick={() =>  
                      document.getElementById("profilePhotoInput").click()  
                    }  
                  >  
                    Change Profile Picture  
                  </Button>  
                  {file && (  
                    <Button  
                      type="primary"  
                      style={{ marginLeft: 10 }}  
                      loading={photoLoading}  
                      onClick={handleFileUpload}  
                    >  
                      Upload  
                    </Button>  
                  )}  
                </div>  
                <Title level={3} style={{ marginBottom: 0 }}>  
                  {contestant.first_name} {contestant.last_name}  
                </Title>  
                <Text>{contestant.email}</Text>  
                <Text>Contestant Number: {contestant.contestant_number}</Text>
              </Space>  
            ) : (  
              <Spin />  
            )}  
          </Card>  
        </Col>  
        <Col span={24} md={16}>  
          <Card  
            title="Your Information"  
            loading={loading}  
            className="profile-info-card"  
          >  
            {contestant && (  
              <Form  
                form={form}  
                layout="vertical"  
                onFinish={handleUpdate}  
                initialValues={{  
                  ...contestant,  
                  date_of_birth: contestant.date_of_birth  
                    ? dayjs(contestant.date_of_birth)  
                    : null,  
                  address: contestant.address?.[0] || {},  
                }}  
              >  
                <Row gutter={16}>  
                  <Col xs={24} sm={12}>  
                    <Form.Item  
                      label="First Name"  
                      name="first_name"  
                      rules={[{ required: true, message: "First name required" }]}  
                    >  
                      <Input />  
                    </Form.Item>  
                    <Form.Item  
                      label="Last Name"  
                      name="last_name"  
                      rules={[{ required: true, message: "Last name required" }]}  
                    >  
                      <Input />  
                    </Form.Item>  
                    <Form.Item  
                      label="Email"  
                      name="email"  
                      rules={[  
                        { required: true, message: "Email required" },  
                        { type: "email", message: "Invalid email" },  
                      ]}  
                    >  
                      <Input />  
                    </Form.Item>  
                    <Form.Item label="Gender" name="gender">  
                      <Input />  
                    </Form.Item>  
                    <Form.Item  
                      label="Cell Number"  
                      name="cell_number"  
                      rules={[  
                        { required: true, message: "Cell number required" },  
                        {  
                          pattern: /^[0-9]{10}$/,  
                          message: "Must be a valid 10 digit cell number",  
                        },  
                      ]}  
                    >  
                      <Input />  
                    </Form.Item>  
                    <Form.Item  
                      label="Work Number"  
                      name="work_number"  
                      rules={[  
                        {  
                          pattern: /^[0-9]{10}$/,  
                          message: "Must be a valid 10 digit work number",  
                        },  
                      ]}  
                    >  
                      <Input />  
                    </Form.Item>  
                    <Form.Item  
                      label="Date of Birth"  
                      name="date_of_birth"  
                      rules={[  
                        { required: true, message: "Date of birth required" },  
                      ]}  
                    >  
                      <DatePicker  
                        format="YYYY-MM-DD"  
                        style={{ width: "100%" }}  
                        disabledDate={d => d && d > dayjs().endOf("day")}  
                      />  
                    </Form.Item>  
                    <Form.Item  
                      label="ID Number"  
                      name="id_number"  
                      rules={[  
                        { required: true, message: "ID number required. If not applicable put 0" },  
                      ]}  
                    >  
                      <Input />  
                    </Form.Item>  
                    <Form.Item label="Passport Number" 
                    name="passport_number"
                    rules={[  
                      { required: true, message: "Passport number required. If not applicable put 0" },  
                    ]}  
                    >  
                      <Input />  
                    </Form.Item>  
                  </Col>  
                  <Col xs={24} sm={12}>  
                    <Card size="small" type="inner" title="Address">  
                      <Form.Item  
                        label="Street 1"  
                        name={["address", "street_1"]}  
                        rules={[  
                          { required: true, message: "Street 1 is required" },  
                        ]}  
                      >  
                        <Input />  
                      </Form.Item>  
                      <Form.Item  
                        label="Street 2"  
                        name={["address", "street_2"]}  
                      >  
                        <Input />  
                      </Form.Item>  
                      <Form.Item  
                        label="Street 3"  
                        name={["address", "street_3"]}  
                      >  
                        <Input />  
                      </Form.Item>  
                      <Form.Item  
                        label="City"  
                        name={["address", "city"]}  
                        rules={[  
                          { required: true, message: "City is required" },  
                        ]}  
                      >  
                        <Input />  
                      </Form.Item>  
                      <Form.Item  
                        label="Province"  
                        name={["address", "province"]}  
                        rules={[  
                          { required: true, message: "Province is required" },  
                        ]}  
                      >  
                        <Input />  
                      </Form.Item>  
                      <Form.Item  
                        label="Country"  
                        name={["address", "country"]}  
                        rules={[  
                          { required: true, message: "Country is required" },  
                        ]}  
                      >  
                        <Input />  
                      </Form.Item>  
                      <Form.Item  
                        label="Postal Code"  
                        name={["address", "postal_code"]}  
                        rules={[  
                          { required: true, message: "Postal code is required" },  
                        ]}  
                      >  
                        <Input />  
                      </Form.Item>  
                    </Card>  
                    <Card  
                      size="small"  
                      type="inner"  
                      title="Parent/Coach Information (Type NO if not applicable)"  
                      style={{ marginTop: 16 }}  
                    >  
                      <Form.Item label="Parent/Coach Name" 
                                 name="ice_contact_name"
                                 rules={[  
                                  { required: true, message: "Type NO if not applicable" },  
                                ]} >  
                        <Input />  
                      </Form.Item>  
                      <Form.Item  
                        label="Relationship to Contestant"  
                        name="relationship_to_ice"  
                        rules={[  
                          { required: true, message: "Type NO if not applicable" },  
                        ]} 
                      >  
                        <Input />  
                      </Form.Item>  
                      <Form.Item  
                        label="Parent/Coach Email"  
                        name="ice_contact_email"
                        rules={[  
                          { required: true, message: "Type NO if not applicable" },  
                        ]}    
                      >  
                        <Input />  
                      </Form.Item>  
                    </Card>  
                    <Form.Item  
                      label="Allergies"  
                      name="allergies"  
                      valuePropName="checked"  
                    >  
                      <Checkbox  
                        onChange={e => setHasAllergies(e.target.checked)}  
                        checked={hasAllergies}  
                      >  
                        Do you have allergies?  
                      </Checkbox>  
                    </Form.Item>  
                    {hasAllergies && (  
                      <Form.Item  
                        label="Allergies Specification"  
                        name="allergies_specification"  
                        rules={[  
                          { required: true, message: "Please specify your allergies" },  
                        ]}  
                      >  
                        <Input />  
                      </Form.Item>  
                    )}   
                    <Form.Item  
                      label="Academic Institution"  
                      name="academic_institution"  
                    >  
                      <Input placeholder="Name of school, college or university (if applicable)" />  
                    </Form.Item>  
                    <Form.Item  
                      label="Provincial Director's Name"  
                      name="provincial_director"  
                    >  
                      <Input placeholder="Provincial Director's Name (if applicable)" />  
                    </Form.Item>  
                  </Col>  
                </Row>  
                <Form.Item>  
                  <Button  
                    type="primary"  
                    htmlType="submit"  
                    loading={loading}  
                    style={{ minWidth: 120 }}  
                  >  
                    Update Information  
                  </Button>  
                </Form.Item>  
              </Form>  
            )}  
          </Card>  
        </Col>  
      </Row>  
    </div>  
  );  
};  
  
export default Profile;  